import React, { useState, useEffect } from "react";
import { FileText, Loader2, CheckCircle, ShieldCheck, AlertCircle } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchInstallmentPlanService,
  acceptInstallmentAgreementOnlineService,
} from "../../../services/users.services";
import { toast } from "react-toastify";

const InstallmentAgreementAcceptanceModal = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const enrolId = localStorage.getItem("enrol_id");
  const studentName = `${localStorage.getItem("username") || ""} ${localStorage.getItem("lastname") || ""}`.trim() || "Student";
  const programName = localStorage.getItem("program_name") || localStorage.getItem("course") || "your program";

  const { data: planData, isLoading } = useQuery({
    queryFn: () => fetchInstallmentPlanService({ enrol_id: enrolId }),
    queryKey: ["installment-plan-agreement-check", enrolId],
    enabled: !!enrolId && enrolId !== "undefined" && enrolId !== "null",
    refetchOnWindowFocus: false,
  });

  const plan = planData?.data?.plan;
  const agreement = planData?.data?.agreement;

  const shouldShow =
    plan?.agreement_file &&
    agreement?.accepted_online_at == null &&
    agreement?.verification_status !== "approved";

  useEffect(() => {
    if (!isLoading && !shouldShow) {
      onClose();
    }
  }, [isLoading, shouldShow]);

  const { mutate: acceptOnline, isLoading: isAccepting } = useMutation({
    mutationFn: () =>
      acceptInstallmentAgreementOnlineService({
        plan_id: plan.id,
        enrol_id: enrolId,
        student_name: studentName,
        program_name: programName,
        student_email: localStorage.getItem("email"),
      }),
    onSuccess: () => {
      setAccepted(true);
      queryClient.invalidateQueries(["installment-plan-agreement-check"]);
      queryClient.invalidateQueries(["installment-plan"]);
      toast.success("Agreement accepted! Your installment plan is now active.");
      setTimeout(() => onClose(), 2000);
    },
    onError: () => {
      toast.error("Something went wrong. Please try again.");
    },
  });

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-30 md:pl-64 pt-20 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center">
        <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          <p className="text-sm font-bold text-gray-800 uppercase tracking-widest">Loading...</p>
        </div>
      </div>
    );
  }

  if (!shouldShow) return null;

  if (accepted) {
    return (
      <div className="fixed inset-0 z-30 md:pl-64 pt-20 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />
        <div className="relative bg-white w-full max-w-md p-10 rounded-2xl shadow-2xl flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center">
            <CheckCircle className="w-9 h-9" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Agreement Accepted!</h2>
          <p className="text-sm text-gray-500">Your installment plan is now active. You will receive a confirmation email shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-30 md:pl-64 pt-20 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-white sticky top-0 z-10">
          <div className="flex w-9 h-9 rounded-xl bg-blue-50 items-center justify-center text-blue-600">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-800 leading-none mb-0.5">Review & Accept Installment Agreement</h2>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Action required before continuing</p>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 bg-gray-100 overflow-hidden" style={{ minHeight: 0 }}>
          <iframe
            src={plan.agreement_file}
            title="Installment Agreement"
            className="w-full h-full"
            style={{ height: "50vh", border: "none" }}
          />
        </div>

        {/* Notice */}
        <div className="px-5 py-3 bg-amber-50 border-y border-amber-100 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800 font-medium leading-relaxed">
            Please read the agreement carefully before accepting. By clicking <strong>Accept Agreement</strong>, you confirm that you have read and agreed to all terms of the installment plan.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer flex-shrink-0"
            />
            <span className="text-sm text-gray-700 font-medium leading-snug">
              I have read and agree to the terms of this installment agreement
            </span>
          </label>

          <button
            disabled={!agreed || isAccepting}
            onClick={() => acceptOnline()}
            className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all
              bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]
              disabled:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            {isAccepting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Accepting...
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" />
                Accept Agreement
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallmentAgreementAcceptanceModal;
